import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWizard } from "../../context/WizardContext";
import Modal from "./Modal";
import { useAuth } from "../../context/AuthContext";
import LogoLoader from "../common/LogoLoader";
import { API_BASE_URL } from "../../config/apiBaseUrl";
import AdminPageNavigator from "../AdminPageNavigator";

const CharacterAnalysis: React.FC = () => {
  const { sessionId, language } = useWizard();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState<string>('');
  const [analysisText, setAnalysisText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isPromptExpanded, setIsPromptExpanded] = useState<boolean>(false);
  const [isPromptCopied, setIsPromptCopied] = useState<boolean>(false);

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    icon?: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  useEffect(() => {
    if (!sessionId) {
      navigate("/create/language");
      return;
    }

    loadCharacterAnalysisPrompt();
    // Re-hydrate previously saved admin paste (if any)
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/stories/${sessionId}`, {
          headers: { ...getAuthHeader() },
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) return;
        const raw = data?.data?.characterAnalysisRaw;
        if (typeof raw === "string" && raw.trim().length > 0) {
          setAnalysisText(raw);
        }
      } catch {
        // ignore
      }
    })();
  }, [sessionId, navigate, getAuthHeader]);

  const loadCharacterAnalysisPrompt = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/character-analysis-prompt`, {
        headers: { ...getAuthHeader() },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load character analysis prompt');
      }

      setPrompt(data.data.prompt);
    } catch (err: any) {
      console.error('Failed to load character analysis prompt:', err);
      setError(err.message || 'Failed to load character analysis prompt');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAnalysis = async () => {
    if (!analysisText.trim()) {
      showModal('error', 'Missing Analysis', 'Please paste the OpenAI response before saving.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/save-character-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ analysisText: analysisText.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save character analysis');
      }

      // Navigate directly to photo training after successful save
      navigate("/create/photo-training");
    } catch (err: any) {
      console.error('Failed to save character analysis:', err);
      setError(err.message || 'Failed to save character analysis');
    } finally {
      setIsSaving(false);
    }
  };

  const showModal = (
    type: 'info' | 'success' | 'warning' | 'error' | 'confirm',
    title: string,
    message: string,
    onConfirm?: () => void,
    confirmText?: string,
    icon?: string
  ) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      confirmText,
      icon
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    closeModal();
  };

  if (isLoading) {
    return (
      <div className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex flex-col items-center px-4 pt-8"
        style={{
          backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/3_background.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        <main id="main-content" tabIndex={-1} className="w-full max-w-4xl outline-none" aria-busy="true">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 shadow-card border border-white/40 max-w-4xl w-full z-10 text-center">
            <LogoLoader size="md" className="mb-4" />
            <p className="text-purple-800 font-medium">Loading character analysis prompt...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex flex-col items-center px-4 pt-8"
      style={{
        backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/3_background.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <main
        id="main-content"
        tabIndex={-1}
        className="relative flex w-full max-w-4xl flex-1 flex-col items-center outline-none"
      >
      <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 pb-24 shadow-card border border-white/40 max-w-4xl w-full z-10">
        <h1 className="text-3xl font-bold text-heading mb-3 text-center">
          Character Analysis
        </h1>
        <AdminPageNavigator align="center" />
        <p className="text-sm text-gray-700 mb-6 text-center">
          Use this prompt in ChatGPT to analyze your story and identify which characters should appear on each page.
        </p>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Prompt Display */}
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="mb-3">
              <div className="mb-2">
                <button
                  type="button"
                  onClick={() => setIsPromptExpanded((prev) => !prev)}
                  className="w-full rounded-lg border border-blue-200 bg-white px-4 py-3 text-left hover:bg-blue-50 transition-colors"
                  aria-expanded={isPromptExpanded}
                  aria-controls="character-analysis-prompt-panel"
                >
                  <span className="flex items-center justify-between">
                    <span>Complete Prompt</span>
                    <span>{isPromptExpanded ? '▼' : '▶'}</span>
                  </span>
                </button>
              </div>
              <div className="mt-3">
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(prompt);
                      setIsPromptCopied(true);
                      window.setTimeout(() => setIsPromptCopied(false), 2000);
                    } catch {
                      setError('Failed to copy prompt to clipboard.');
                    }
                  }}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg text-base font-semibold hover:bg-purple-700"
                >
                  {isPromptCopied ? '✅ Copied' : '📋 Copy Prompt'}
                </button>
              </div>
            </div>
            {isPromptExpanded && (
              <pre
                id="character-analysis-prompt-panel"
                className="whitespace-pre-wrap text-sm text-gray-800 bg-white p-4 rounded border max-h-96 overflow-y-auto"
              >
                {prompt}
              </pre>
            )}
          </div>

          {/* Analysis Response Input */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">OpenAI Response</h2>
            <p id="character-analysis-input-hint" className="text-sm text-gray-600 mb-3">
              Paste the response from OpenAI here. The format should be one line per page, like:
              <br />
              <code className="bg-gray-200 px-2 py-1 rounded">1: Character1, Character2</code>
              <br />
              <code className="bg-gray-200 px-2 py-1 rounded">2: Character1, Character3</code>
            </p>
            <label htmlFor="character-analysis-response" className="sr-only">
              OpenAI response text
            </label>
            <textarea
              id="character-analysis-response"
              value={analysisText}
              onChange={(e) => setAnalysisText(e.target.value)}
              placeholder="Paste OpenAI response here..."
              aria-describedby="character-analysis-input-hint"
              className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate("/create/story-studio")}
              className="bg-white/90 hover:bg-white text-heading border-2 border-brandNavy/30 font-semibold px-6 py-3 rounded-2xl text-lg shadow-soft hover:shadow-card transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
            >
              ← Back to Story Studio
            </button>
            <button
              onClick={handleSaveAnalysis}
              disabled={isSaving || !analysisText.trim()}
              className="bg-brandOrange hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-2xl text-lg shadow-card hover:shadow-glow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
            >
              {isSaving ? 'Saving...' : 'Save Analysis & Continue'}
            </button>
          </div>
        </div>
      </div>
      </main>

      {/* Modal */}
      <Modal
        isOpen={modalState.isOpen}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
        onConfirm={handleModalConfirm}
        confirmText={modalState.confirmText}
        icon={modalState.icon}
        onClose={closeModal}
      />
    </div>
  );
};

export default CharacterAnalysis;



